import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { leavesApi, leavePoliciesApi } from '../../services/api';
import useStore from '../../store';
import './Leaves.css';

const BASE_LEAVE_TYPES = ['earned', 'sick', 'Special'];
const MENSTRUAL_MONTHLY_LIMIT = 1;

const STATUS_BADGE = {
  pending:   'badge-amber',
  approved:  'badge-emerald',
  rejected:  'badge-rose',
  cancelled: 'badge-gray',
};

const getStatusLabel = (leave) => {
  if (leave?.cancellation_requested) return 'cancel pending';
  if (leave?.status === 'pending' && leave?.reviewed_by) return 'pending super admin';
  return leave?.status;
};

const formatLeaveType = (leaveType) => {
  const normalized = String(leaveType || '').toLowerCase();
  if (normalized === 'wfh') return 'WFH';
  if (normalized === 'menstrual_leaves') return 'Menstrual Leaves';
  if (normalized === 'special') return 'Special';
  if (!leaveType) return '';
  return leaveType.charAt(0).toUpperCase() + leaveType.slice(1);
};

const getBusinessDays = (startDate, endDate, isHalfDay) => {
  if (isHalfDay) return 0.5;
  const start = new Date(startDate);
  const end = new Date(endDate);
  let days = 0;
  const current = new Date(start);
  while (current <= end) {
    const dow = current.getDay();
    if (dow !== 0 && dow !== 6) days++;
    current.setDate(current.getDate() + 1);
  }
  return days;
};

export default function LeavesPage() {
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','manager'].includes(user?.role);
  const [tab, setTab] = useState('my');

  // My requests
  const [myRequests, setMyRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [policy, setPolicy] = useState(null);

  // Team requests (manager/admin)
  const [teamRequests, setTeamRequests] = useState([]);
  const [teamFilter, setTeamFilter] = useState('pending');

  // Apply modal
  const [showApply, setShowApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({
    leave_type: 'earned', start_date: '', end_date: '',
    half_day: false, half_day_period: 'first_half', reason: '',
  });

  // Review modal
  const [reviewModal, setReviewModal] = useState(null); // { leave, action }
  const [reviewComment, setReviewComment] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const getRequestedDays = () => {
    if (!form.start_date || !form.end_date) return 0;
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24) + 1;
    return form.half_day ? 0.5 : Math.max(daysDiff, 0);
  };

  const load = useCallback(async () => {
    const year = new Date().getFullYear();
    const [myRes, balRes] = await Promise.all([
      leavesApi.list({ limit: 50 }),
      leavesApi.balance({ year }),
    ]);
    // Only show current user's requests in 'My Requests'
    setMyRequests(myRes?.data?.data?.filter((r) => r?.user_id === user?.id));
    setBalances(balRes.data.data);
    useStore.getState().setTakenSickLeave(balRes.data.takenSickLeave);
    useStore.getState().setTakenSpecialLeave(balRes.data.takenSpecialLeave);

    // Load user's assigned leave policy (if any) and show policy-based balances
    try {
      if (user?.leave_policy_id) {
        const pol = await leavePoliciesApi.get(user.leave_policy_id);
        setPolicy(pol.data.data);
      } else {
        setPolicy(null);
      }
    } catch (err) {
      // ignore policy fetch errors, fallback to global balances
      setPolicy(null);
    }

    if (isManager) {
      const teamRes = await leavesApi.list({ status: teamFilter === 'all' ? undefined : teamFilter, limit: 50 });
      setTeamRequests(teamRes.data.data.filter((r) => r.user_id !== user.id));
    }
  }, [isManager, teamFilter, user?.id, user?.leave_policy_id]);

  useEffect(() => { load(); }, [load]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date || !form.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    if (form.leave_type.toLowerCase() === 'birthday') {
      if (!user?.date_of_birth) {
        toast.error('Please update your date of birth before requesting birthday leave');
        return;
      }
      if (form.start_date !== form.end_date) {
        toast.error('Birthday leave must be for a single day');
        return;
      }
      const dobMonthDay = String(user.date_of_birth).slice(5, 10);
      const reqMonthDay = String(form.start_date).slice(5, 10);
      if (dobMonthDay !== reqMonthDay) {
        toast.error('Birthday leave must be on your date of birth');
        return;
      }
    }

    if (form.leave_type.toLowerCase() === 'wfh') {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);
      if (start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth()) {
        toast.error('WFH requests must be within a single month');
        return;
      }

      const requestedDays = getBusinessDays(form.start_date, form.end_date, form.half_day);
      const usedDays = myRequests
        .filter((r) => String(r?.leave_type || '').toLowerCase() === 'wfh')
        .filter((r) => !['rejected', 'cancelled'].includes(r.status))
        .filter((r) => {
          const d = new Date(r.start_date);
          return d.getFullYear() === start.getFullYear() && d.getMonth() === start.getMonth();
        })
        .reduce((sum, r) => sum + parseFloat(r.days_requested || 0), 0);

      if (requestedDays + usedDays > 5) {
        const remaining = Math.max(5 - usedDays, 0);
        toast.error(`WFH limit is 5 days per month. Remaining this month: ${remaining}.`);
        return;
      }
    }

    if (form.leave_type.toLowerCase() === 'menstrual_leaves') {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);
      if (start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth()) {
        toast.error('Menstrual leave must be within a single month');
        return;
      }

      const requestedDays = getBusinessDays(form.start_date, form.end_date, form.half_day);
      const usedDays = myRequests
        .filter((r) => String(r?.leave_type || '').toLowerCase() === 'menstrual_leaves')
        .filter((r) => !['rejected', 'cancelled'].includes(r.status))
        .filter((r) => {
          const d = new Date(r.start_date);
          return d.getFullYear() === start.getFullYear() && d.getMonth() === start.getMonth();
        })
        .reduce((sum, r) => sum + parseFloat(r.days_requested || 0), 0);

      if (requestedDays + usedDays > MENSTRUAL_MONTHLY_LIMIT) {
        const remaining = Math.max(MENSTRUAL_MONTHLY_LIMIT - usedDays, 0);
        toast.error(`Menstrual leave is limited to ${MENSTRUAL_MONTHLY_LIMIT} day per month. Remaining this month: ${remaining}.`);
        return;
      }
    }

    // Validation: check against policy max if user has policy
    const isPolicyMode = !!user?.leave_policy_id;
    if (isPolicyMode && form.leave_type.toLowerCase() === 'earned') {
      const earnedFromPolicy = policy?.balances?.find((b) => b.leave_type === 'earned');
      if (earnedFromPolicy) {
        const allocatedDays = parseFloat(earnedFromPolicy.allocated_days || 0);
        const remainingBalance = allocatedDays - (earnedFromPolicy.taken || 0);
        const daysToRequest = getRequestedDays();
        if (daysToRequest > remainingBalance) {
          toast.error(`Cannot request more than ${remainingBalance} earned leave days available in your policy`);
          return;
        }
      }
    }

    setApplying(true);
    try {
      await leavesApi.create(form);
      toast.success('Leave request submitted!');
      setShowApply(false);
      setForm({ leave_type: 'earned', start_date: '', end_date: '', half_day: false, half_day_period: 'first_half', reason: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit leave');
    } finally {
      setApplying(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      await leavesApi.cancel(cancelModal.id);
      toast.success('Cancellation request sent for approval');
      setCancelModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    setReviewing(true);
    try {
      const res = await leavesApi.review(reviewModal.leave.id, {
        status: reviewModal.action,
        reviewer_comment: reviewComment,
      });
      const updatedLeave = res.data.data;
      const isStageOneApproval =
        updatedLeave.status === 'pending' &&
        updatedLeave.reviewed_by &&
        !updatedLeave.cancellation_requested;

      if (isStageOneApproval) {
        toast.success('Manager approval saved. Awaiting super admin approval.');
      } else {
        const entity = reviewModal.leave.cancellation_requested ? 'Cancellation request' : 'Leave';
        toast.success(`${entity} ${reviewModal.action}!`);
      }
      setReviewModal(null);
      setReviewComment('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Review failed');
    } finally {
      setReviewing(false);
    }
  };

  // Determine if we should display policy-based balances
  const isPolicyMode = !!user?.leave_policy_id;
  
  // Create display balances by merging policy template with the user's actual balances
  const displayBalances = isPolicyMode && policy?.balances?.length
    ? policy.balances.map((pb) => {
        const userBal = balances.find((b) => String(b.leave_type || '').toLowerCase() === String(pb.leave_type || '').toLowerCase());
        const totalAllocated = parseFloat(pb.allocated_days || 0);
        const rollover = parseFloat(userBal?.rollover || 0) || 0;
        const taken = parseFloat(userBal?.taken || 0) || 0;
        const balance = parseFloat((totalAllocated + rollover - taken).toFixed(2));
        return {
          // prefer the user's balance record id when available
          id: userBal?.id || `policy-${pb.leave_type}`,
          leave_type: pb.leave_type,
          total_allocated: totalAllocated,
          rollover,
          taken,
          balance,
        };
      })
    : balances;

  const balanceMap = Object.fromEntries(displayBalances.map((b) => [b.leave_type, b]));
  const policyLeaveTypes = useMemo(
    () => (policy?.balances || []).map((b) => b.leave_type),
    [policy]
  );
  const availableLeaveTypes = useMemo(
    () => (isPolicyMode && policyLeaveTypes.length ? policyLeaveTypes : BASE_LEAVE_TYPES),
    [isPolicyMode, policyLeaveTypes]
  );

  useEffect(() => {
    if (availableLeaveTypes.length && !availableLeaveTypes.includes(form.leave_type)) {
      setForm((prev) => ({ ...prev, leave_type: availableLeaveTypes[0] }));
    }
  }, [availableLeaveTypes, form.leave_type]);

  return (
    <div className="leaves-page">
      <div className="page-header">
        <div>
          <div className="page-title">Leave Management</div>
          <div className="page-subtitle">
            {'Track and manage your leave requests'}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowApply(true)}>
          + Apply for Leave
        </button>
      </div>

      {/* Balance strip */}
      <div className="leave-balance-strip animate-in">
        {displayBalances.map((b) => (
          <div key={b.id} className="balance-chip">
            <span className="balance-chip-type">{formatLeaveType(b.leave_type)} Leaves</span>
            <span className="balance-chip-num">{b.balance ?? parseFloat(b.total_allocated) + parseFloat(b.rollover || 0) - parseFloat(b.taken || 0)}</span>
            <span className="balance-chip-label">available</span>
          </div>
        ))}
        <div key="sick" className="balance-chip">
          <span className="balance-chip-type">Sick Leaves</span>
          <span className="balance-chip-num">{useStore.getState().takenSickLeave ?? 0}</span>
          <span className="balance-chip-label">taken</span>
        </div>
        <div key="special" className="balance-chip">
          <span className="balance-chip-type">Special Leaves</span>
          <span className="balance-chip-num">{useStore.getState().takenSpecialLeave ?? 0}</span>
          <span className="balance-chip-label">taken</span>
        </div>
      </div>

      {/* Tabs */}
      {isManager && (
        <div className="leaves-tabs animate-in stagger-1">
          <button className={`tab-btn ${tab === 'my' ? 'tab-btn--active' : ''}`} onClick={() => setTab('my')}>My Requests</button>
          <button className={`tab-btn ${tab === 'team' ? 'tab-btn--active' : ''}`} onClick={() => setTab('team')}>
            Team Requests
            {teamRequests.filter((r) => r.status === 'pending').length > 0 && (
              <span className="tab-badge">{teamRequests.filter((r) => r.status === 'pending').length}</span>
            )}
          </button>
        </div>
      )}

      {/* My requests */}
      {tab === 'my' && (
        <div className="card animate-in stagger-2">
          {myRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">☂️</div>
              <div className="empty-state-title">No leave requests yet</div>
              <div className="empty-state-text">Click "Apply for Leave" to get started</div>
            </div>
          ) : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr>
                  <th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Reviewer</th><th></th>
                </tr></thead>
                <tbody>
                  {myRequests.map((lr) => (
                    <tr key={lr.id}>
                      <td><span className="leave-type-pill">{formatLeaveType(lr.leave_type)}</span></td>
                      <td>{new Date(lr.start_date).toLocaleDateString('en-IN')}</td>
                      <td>{new Date(lr.end_date).toLocaleDateString('en-IN')}</td>
                      <td><strong>{lr.days_requested}</strong></td>
                      <td style={{ maxWidth: 200 }} className="truncate" title={lr.reason}>{lr.reason}</td>
                      <td><span className={`badge ${STATUS_BADGE[lr.status]}`}>{getStatusLabel(lr)}</span></td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                        {lr.reviewer ? `${lr.reviewer.first_name} ${lr.reviewer.last_name}` : '—'}
                        {lr.reviewer_comment && (
                          <div className="reviewer-comment" title={lr.reviewer_comment}>💬 {lr.reviewer_comment.slice(0,30)}…</div>
                        )}
                      </td>
                      <td>
                        {['pending','approved'].includes(lr.status) && !lr.cancellation_requested && (
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-rose)' }}
                            onClick={() => setCancelModal(lr)}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Team requests */}
      {tab === 'team' && isManager && (
        <div className="card animate-in stagger-2">
          <div className="card-header" style={{ paddingBottom: 12 }}>
            <h3>Team Leave Requests</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {['pending','approved','rejected','all'].map((f) => (
                <button key={f} className={`btn btn-sm ${teamFilter === f ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTeamFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {teamRequests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <div className="empty-state-title">No requests to review</div>
            </div>
          ) : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr>
                  <th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {teamRequests.map((lr) => (
                    <tr key={lr.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar avatar-sm">
                            {lr.requester?.first_name?.[0]}{lr.requester?.last_name?.[0]}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{lr.requester?.first_name} {lr.requester?.last_name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{lr.requester?.designation}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="leave-type-pill">{formatLeaveType(lr.leave_type)}</span></td>
                      <td>{new Date(lr.start_date).toLocaleDateString('en-IN')}</td>
                      <td>{new Date(lr.end_date).toLocaleDateString('en-IN')}</td>
                      <td><strong>{lr.days_requested}</strong></td>
                      <td style={{ maxWidth: 180 }} className="truncate" title={lr.reason}>{lr.reason}</td>
                      <td><span className={`badge ${STATUS_BADGE[lr.status]}`}>{getStatusLabel(lr)}</span></td>
                      <td>
                        {lr.status === 'pending' && (lr.cancellation_requested || user?.role === 'admin' || !lr.reviewed_by) && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm" style={{ background: '#D1FAE5', color: '#065F46', border: 'none' }}
                              onClick={() => { setReviewModal({ leave: lr, action: 'approved' }); setReviewComment(''); }}>
                              ✓ Approve
                            </button>
                            <button className="btn btn-sm" style={{ background: '#FEE2E2', color: '#991B1B', border: 'none' }}
                              onClick={() => { setReviewModal({ leave: lr, action: 'rejected' }); setReviewComment(''); }}>
                              ✕ Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Apply modal */}
      {showApply && (
        <div className="modal-overlay" onClick={() => setShowApply(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Apply for Leave</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowApply(false)}>✕</button>
            </div>
            <form onSubmit={handleApply}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Leave Type *</label>
                  <select className="form-select" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>
                    {availableLeaveTypes.map((t) => (
                      <option key={t} value={t}>
                        {formatLeaveType(t)}
                        {!['sick', 'special'].includes(t.toLowerCase()) && balanceMap[t] ? ` (${balanceMap[t].balance ?? '?'} available)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Start Date *</label>
                    <input type="date" className="form-input" value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.leave_type.toLowerCase() === 'birthday' ? e.target.value : form.end_date })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date *</label>
                    <input type="date" className="form-input" value={form.leave_type.toLowerCase() === 'birthday' ? form.start_date : form.end_date}
                      min={form.start_date || undefined}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      disabled={form.leave_type.toLowerCase() === 'birthday'}
                      required />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} />
                    Half Day
                  </label>
                </div>
                {form.half_day && (
                  <div className="form-group">
                    <label className="form-label">Half Day Period</label>
                    <select className="form-select" value={form.half_day_period} onChange={(e) => setForm({ ...form, half_day_period: e.target.value })}>
                      <option value="first_half">First Half (Morning)</option>
                      <option value="second_half">Second Half (Afternoon)</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">{form.leave_type.toLowerCase() === 'special' ? 'Special Leave Type & Reason *' : 'Reason *'}</label>
                  <textarea className="form-textarea" placeholder={form.leave_type.toLowerCase() === 'special' ? "Specify the type of special leave and your reason..." : "Reason for leave…"} value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowApply(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={applying}>
                  {applying ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {reviewModal.action === 'approved'
                  ? reviewModal.leave.cancellation_requested ? '✅ Approve Cancellation' : '✅ Approve Leave'
                  : reviewModal.leave.cancellation_requested ? '❌ Reject Cancellation' : '❌ Reject Leave'}
              </h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setReviewModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', marginBottom: 12, color: 'var(--text-secondary)' }}>
                <strong>{reviewModal.leave.requester?.first_name} {reviewModal.leave.requester?.last_name}</strong> —{' '}
                {formatLeaveType(reviewModal.leave.leave_type)} leave{reviewModal.leave.cancellation_requested ? ' cancellation' : ''},{' '}
                {new Date(reviewModal.leave.start_date).toLocaleDateString('en-IN')} to{' '}
                {new Date(reviewModal.leave.end_date).toLocaleDateString('en-IN')} ({reviewModal.leave.days_requested} days)
              </p>
              <div className="form-group">
                <label className="form-label">Comment (optional)</label>
                <textarea className="form-textarea" value={reviewComment}
                  placeholder="Add a message to the employee…"
                  onChange={(e) => setReviewComment(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReviewModal(null)}>Cancel</button>
              <button
                className={`btn ${reviewModal.action === 'approved' ? 'btn-primary' : 'btn-danger'}`}
                onClick={handleReview}
                disabled={reviewing}
              >
                {reviewing ? 'Saving…' : reviewModal.action === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancelModal && (
        <div className="modal-overlay" onClick={() => setCancelModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Leave Request</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setCancelModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', marginBottom: 12, color: 'var(--text-secondary)' }}>
                This will send a cancellation request to your manager for approval.
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <strong>{formatLeaveType(cancelModal.leave_type)}</strong> leave from{' '}
                {new Date(cancelModal.start_date).toLocaleDateString('en-IN')} to{' '}
                {new Date(cancelModal.end_date).toLocaleDateString('en-IN')} ({cancelModal.days_requested} days)
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCancelModal(null)} disabled={cancelling}>Keep Request</button>
              <button className="btn btn-danger" onClick={handleCancel} disabled={cancelling}>
                {cancelling ? 'Sending…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
